import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
import { Card } from '../ui/Card';
import { Building2, Link as LinkIcon, Bot, Key, CreditCard, CheckCircle2, ChevronRight, ChevronLeft, Activity, SearchCheck, BarChart3, Lock, FileText, Rocket, Palette, Blocks, Copy, Check, Shield, AlertTriangle } from 'lucide-react';
interface OnboardingPanelProps {
  onComplete: () => void;
}
const TOTAL_STEPS = 6;
const steps = [{
  id: 1,
  label: 'Empresa',
  icon: Building2,
  color: 'text-violet-400'
}, {
  id: 2,
  label: 'Integraciones',
  icon: LinkIcon,
  color: 'text-blue-400'
}, {
  id: 3,
  label: 'Agentes',
  icon: Bot,
  color: 'text-emerald-400'
}, {
  id: 4,
  label: 'Pago',
  icon: CreditCard,
  color: 'text-amber-400'
}, {
  id: 5,
  label: 'API Key',
  icon: Key,
  color: 'text-cyan-400'
}, {
  id: 6,
  label: 'Listo',
  icon: CheckCircle2,
  color: 'text-emerald-400'
}];
const agentsList = [{
  id: 'scrum',
  name: 'Scrum Master Pro',
  specialty: 'Agile & Sprints',
  icon: Activity,
  color: 'text-emerald-400',
  price: 0.02,
  status: 'available'
}, {
  id: 'qa',
  name: 'QA Inspector',
  specialty: 'Testing & Bugs',
  icon: SearchCheck,
  color: 'text-rose-400',
  price: 0.03,
  status: 'available'
}, {
  id: 'analytics',
  name: 'Data Analyst',
  specialty: 'Metrics & BI',
  icon: BarChart3,
  color: 'text-cyan-400',
  price: 0.02,
  status: 'available'
}, {
  id: 'security',
  name: 'Security Guardian',
  specialty: 'Compliance',
  icon: Lock,
  color: 'text-emerald-400',
  price: 0.05,
  status: 'busy'
}, {
  id: 'docs',
  name: 'Doc Writer',
  specialty: 'Knowledge Base',
  icon: FileText,
  color: 'text-amber-400',
  price: 0.01,
  status: 'available'
}, {
  id: 'devops',
  name: 'DevOps Engineer',
  specialty: 'CI/CD & Infra',
  icon: Rocket,
  color: 'text-orange-400',
  price: 0.04,
  status: 'available'
}, {
  id: 'ux',
  name: 'UX Reviewer',
  specialty: 'Design & A11y',
  icon: Palette,
  color: 'text-violet-400',
  price: 0.03,
  status: 'coming_soon'
}, {
  id: 'architect',
  name: 'System Architect',
  specialty: 'Architecture',
  icon: Blocks,
  color: 'text-sky-400',
  price: 0.06,
  status: 'busy'
}];
// Auto-format card number: 1234567890123456 → 1234 5678 9012 3456
function formatCardNumber(val: string) {
  const digits = val.replace(/\D/g, '').slice(0, 16);
  return digits.replace(/(.{4})/g, '$1 ').trim();
}
// Auto-format expiry: 1234 → 12/34
function formatExpiry(val: string) {
  const digits = val.replace(/\D/g, '').slice(0, 4);
  if (digits.length > 2) return digits.slice(0, 2) + '/' + digits.slice(2);
  return digits;
}
// Detect card brand from first digits
function detectCardBrand(num: string): string {
  const d = num.replace(/\D/g, '');
  if (d.startsWith('4')) return 'VISA';
  if (d.startsWith('5') || d.startsWith('2')) return 'MC';
  if (d.startsWith('3')) return 'AMEX';
  return '';
}
export function OnboardingPanel({
  onComplete
}: OnboardingPanelProps) {
  const [currentStep, setCurrentStep] = useState(1);
  const [direction, setDirection] = useState(1); // 1 = forward, -1 = back
  // Step 1: Company
  const [companyName, setCompanyName] = useState('');
  const [industry, setIndustry] = useState('');
  const [teamSize, setTeamSize] = useState('');
  const [methodology, setMethodology] = useState('Scrum');
  // Step 2: Integrations
  const [integrations, setIntegrations] = useState({
    jira: true,
    confluence: false,
    slack: false,
    github: false
  });
  const [testingIntegrations, setTestingIntegrations] = useState(false);
  const [integrationResults, setIntegrationResults] = useState<Record<string, boolean>>({});
  // Step 3: Agents
  const [selectedAgents, setSelectedAgents] = useState<string[]>(['scrum']);
  // Step 4: Payment
  const [cardNumber, setCardNumber] = useState('');
  const [cardExpiry, setCardExpiry] = useState('');
  const [cardCvc, setCardCvc] = useState('');
  const [cardName, setCardName] = useState('');
  const [skippedPayment, setSkippedPayment] = useState(false);
  // Step 5: API Key
  const [copied, setCopied] = useState(false);
  const apiKey = 'vsm_sk_live_9f8a7b6c5d4e3f2a1b0c9d8e7f6a5b4c';
  const goNext = () => {
    setDirection(1);
    setCurrentStep(prev => Math.min(prev + 1, TOTAL_STEPS));
  };
  const goBack = () => {
    setDirection(-1);
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };
  const testConnections = () => {
    setTestingIntegrations(true);
    setTimeout(() => {
      setTestingIntegrations(false);
      setIntegrationResults({
        jira: true,
        confluence: true
      });
    }, 2000);
  };
  const toggleAgent = (id: string, status: string) => {
    if (status === 'coming_soon') return;
    setSelectedAgents(prev => prev.includes(id) ? prev.filter(a => a !== id) : [...prev, id]);
  };
  const copyApiKey = () => {
    navigator.clipboard.writeText(apiKey);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const cardBrand = detectCardBrand(cardNumber);
  const isPaymentValid = cardNumber.replace(/\D/g, '').length >= 15 && cardExpiry.length >= 4 && cardCvc.length >= 3 && cardName.length >= 2;
  const slideVariants = {
    enter: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? 30 : -30
    }),
    center: {
      opacity: 1,
      x: 0
    },
    exit: (dir: number) => ({
      opacity: 0,
      x: dir > 0 ? -30 : 30
    })
  };
  return <div className="h-full overflow-y-auto p-4 lg:p-8 animate-in fade-in duration-300 bg-slate-950 text-slate-200 flex flex-col items-center" data-id="element-5453">
      {/* Progress Bar */}
      <div className="w-full max-w-3xl mb-10 mt-4" data-id="element-5454">
        <div className="flex items-center justify-between relative" data-id="element-5455">
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-full h-0.5 bg-slate-800 -z-10" data-id="element-5456" />
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-0.5 bg-violet-500 -z-10 transition-all duration-500" style={{
          width: `${(currentStep - 1) / (TOTAL_STEPS - 1) * 100}%`
        }} data-id="element-5457" />

          {steps.map(step => {
          const isCompleted = currentStep > step.id;
          const isCurrent = currentStep === step.id;
          return <div key={step.id} className="flex flex-col items-center gap-2" data-id="element-5458">
                <div className={`w-9 h-9 rounded-full flex items-center justify-center border-2 transition-colors duration-300 text-sm ${isCompleted ? 'bg-violet-500 border-violet-500 text-white' : isCurrent ? 'bg-slate-900 border-violet-500 text-violet-400' : 'bg-slate-900 border-slate-700 text-slate-500'}`} data-id="element-5459">
                  {isCompleted ? <CheckCircle2 size={18} data-id="element-5460" /> : <span data-id="element-5461">{step.id}</span>}
                </div>
                <span className={`text-[10px] font-medium hidden sm:block ${isCurrent ? 'text-slate-200' : 'text-slate-500'}`} data-id="element-5462">
                  {step.label}
                </span>
              </div>;
        })}
        </div>
      </div>

      {/* Main Content */}
      <div className="w-full max-w-2xl relative min-h-[520px]" data-id="element-5463">
        <AnimatePresence mode="wait" custom={direction} data-id="element-5464">
          {/* STEP 1: EMPRESA */}
          {currentStep === 1 && <motion.div key="step1" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5465">
              <div className="flex items-center gap-4 mb-8" data-id="element-5466">
                <div className="w-12 h-12 rounded-full bg-violet-500/20 flex items-center justify-center text-violet-400" data-id="element-5467">
                  <Building2 size={24} data-id="element-5468" />
                </div>
                <div data-id="element-5469">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5470">
                    Configurá tu Empresa
                  </h2>
                  <p className="text-slate-400" data-id="element-5471">
                    Contanos sobre tu equipo para adaptar los agentes.
                  </p>
                </div>
              </div>

              <div className="space-y-6" data-id="element-5472">
                <div data-id="element-5473">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5474">
                    Nombre de la Empresa
                  </label>
                  <input value={companyName} onChange={e => setCompanyName(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-violet-500" placeholder="Ej: Acme Corp" data-id="element-5475" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-5476">
                  <div data-id="element-5477">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5478">
                      Industria
                    </label>
                    <select value={industry} onChange={e => setIndustry(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-violet-500 appearance-none" data-id="element-5479">
                      <option value="" data-id="element-5480">Seleccionar...</option>
                      <option value="tech" data-id="element-5481">Tecnología</option>
                      <option value="fin" data-id="element-5482">Finanzas</option>
                      <option value="logistics" data-id="element-5483">Logística</option>
                      <option value="health" data-id="element-5484">Salud</option>
                      <option value="legal" data-id="element-5485">Legal</option>
                      <option value="retail" data-id="element-5486">Retail</option>
                      <option value="other" data-id="element-5487">Otro</option>
                    </select>
                  </div>
                  <div data-id="element-5488">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5489">
                      Metodología
                    </label>
                    <select value={methodology} onChange={e => setMethodology(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-violet-500 appearance-none" data-id="element-5490">
                      <option value="Scrum" data-id="element-5491">Scrum</option>
                      <option value="Kanban" data-id="element-5492">Kanban</option>
                      <option value="SAFe" data-id="element-5493">SAFe</option>
                      <option value="Custom" data-id="element-5494">Custom</option>
                    </select>
                  </div>
                </div>
                <div data-id="element-5495">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5496">
                    Tamaño del Equipo
                  </label>
                  <div className="grid grid-cols-4 gap-3" data-id="element-5497">
                    {['1-5', '6-20', '21-50', '50+'].map(size => <button key={size} onClick={() => setTeamSize(size)} className={`py-2 rounded-lg border text-sm font-medium transition-all ${teamSize === size ? 'bg-violet-500/20 border-violet-500 text-violet-300' : 'bg-slate-950 border-slate-700 text-slate-400 hover:border-slate-600'}`} data-id="element-5498">
                        {size}
                      </button>)}
                  </div>
                </div>
              </div>

              <div className="mt-10 flex justify-end" data-id="element-5499">
                <Button onClick={goNext} disabled={!companyName || !industry} className="bg-violet-600 hover:bg-violet-500 px-8" data-id="element-5500">
                  Continuar <ChevronRight size={16} className="ml-2" data-id="element-5501" />
                </Button>
              </div>
            </motion.div>}

          {/* STEP 2: INTEGRACIONES */}
          {currentStep === 2 && <motion.div key="step2" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5502">
              <div className="flex items-center gap-4 mb-8" data-id="element-5503">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400" data-id="element-5504">
                  <LinkIcon size={24} data-id="element-5505" />
                </div>
                <div data-id="element-5506">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5507">
                    Conectá tus herramientas
                  </h2>
                  <p className="text-slate-400" data-id="element-5508">
                    VSM necesita acceso a tu plataforma para operar.
                  </p>
                </div>
              </div>

              <div className="space-y-3 mb-8" data-id="element-5509">
                {/* Jira */}
                <div className={`border rounded-xl p-4 transition-all ${integrations.jira ? 'bg-slate-950 border-blue-500/50' : 'bg-slate-950/50 border-slate-800'}`} data-id="element-5510">
                  <div className="flex items-center justify-between mb-2" data-id="element-5511">
                    <div className="flex items-center gap-3" data-id="element-5512">
                      <div className="w-8 h-8 rounded bg-blue-900/30 flex items-center justify-center text-blue-400" data-id="element-5513">
                        <Activity size={16} data-id="element-5514" />
                      </div>
                      <p className="font-semibold text-slate-200 flex items-center gap-2" data-id="element-5515">
                        Jira Software{' '}
                        <Badge className="bg-blue-500/20 text-blue-300 border-0 text-[10px]" data-id="element-5516">
                          Recomendado
                        </Badge>
                      </p>
                    </div>
                    <button onClick={() => setIntegrations(p => ({
                  ...p,
                  jira: !p.jira
                }))} className={`w-10 h-5 rounded-full relative transition-colors ${integrations.jira ? 'bg-blue-500' : 'bg-slate-700'}`} data-id="element-5517">
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${integrations.jira ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-5518" />
                    </button>
                  </div>
                  {integrations.jira && <div className="mt-3 space-y-3 pt-3 border-t border-slate-800" data-id="element-5519">
                      <input className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500" placeholder="Base URL (ej: https://acme.atlassian.net)" data-id="element-5520" />

                      <div className="grid grid-cols-2 gap-3" data-id="element-5521">
                        <input className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500" placeholder="Email" data-id="element-5522" />

                        <input type="password" className="w-full bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-slate-200 focus:outline-none focus:border-blue-500" placeholder="API Token" data-id="element-5523" />
                      </div>
                    </div>}
                </div>

                {/* Confluence */}
                <div className={`border rounded-xl p-4 transition-all ${integrations.confluence ? 'bg-slate-950 border-blue-500/50' : 'bg-slate-950/50 border-slate-800'}`} data-id="element-5524">
                  <div className="flex items-center justify-between" data-id="element-5525">
                    <div className="flex items-center gap-3" data-id="element-5526">
                      <div className="w-8 h-8 rounded bg-sky-900/30 flex items-center justify-center text-sky-400" data-id="element-5527">
                        <FileText size={16} data-id="element-5528" />
                      </div>
                      <p className="font-semibold text-slate-200" data-id="element-5529">Confluence</p>
                    </div>
                    <button onClick={() => setIntegrations(p => ({
                  ...p,
                  confluence: !p.confluence
                }))} className={`w-10 h-5 rounded-full relative transition-colors ${integrations.confluence ? 'bg-blue-500' : 'bg-slate-700'}`} data-id="element-5530">
                      <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${integrations.confluence ? 'translate-x-5' : 'translate-x-0'}`} data-id="element-5531" />
                    </button>
                  </div>
                </div>
              </div>

              <div className="flex items-center justify-between" data-id="element-5532">
                <div className="flex gap-3" data-id="element-5533">
                  <Button variant="outline" size="sm" onClick={goBack} className="border-slate-700 text-slate-400" data-id="element-5534">
                    <ChevronLeft size={16} data-id="element-5535" />
                  </Button>
                  <Button variant="outline" onClick={testConnections} isLoading={testingIntegrations} className="border-slate-700 text-slate-300" data-id="element-5536">
                    Probar Conexiones
                  </Button>
                </div>
                <div className="flex gap-4 items-center" data-id="element-5537">
                  <button onClick={goNext} className="text-sm text-slate-500 hover:text-slate-300" data-id="element-5538">
                    Omitir
                  </button>
                  <Button onClick={goNext} className="bg-blue-600 hover:bg-blue-500 px-8" data-id="element-5539">
                    Continuar <ChevronRight size={16} className="ml-2" data-id="element-5540" />
                  </Button>
                </div>
              </div>
            </motion.div>}

          {/* STEP 3: AGENTES */}
          {currentStep === 3 && <motion.div key="step3" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5541">
              <div className="flex items-center gap-4 mb-6" data-id="element-5542">
                <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400" data-id="element-5543">
                  <Bot size={24} data-id="element-5544" />
                </div>
                <div data-id="element-5545">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5546">
                    Armá tu equipo de agentes IA
                  </h2>
                  <p className="text-slate-400" data-id="element-5547">
                    Seleccioná los especialistas que necesitás.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3 mb-6 max-h-[280px] overflow-y-auto pr-2" data-id="element-5548">
                {agentsList.map(agent => {
              const Icon = agent.icon;
              const isSelected = selectedAgents.includes(agent.id);
              const isComingSoon = agent.status === 'coming_soon';
              return <div key={agent.id} onClick={() => toggleAgent(agent.id, agent.status)} className={`p-3 rounded-xl border cursor-pointer transition-all ${isComingSoon ? 'opacity-50 grayscale cursor-not-allowed bg-slate-950/50 border-slate-800' : isSelected ? 'bg-emerald-950/20 border-emerald-500/50 ring-1 ring-emerald-500/30' : 'bg-slate-950 border-slate-800 hover:border-slate-700'}`} data-id="element-5549">
                      <div className="flex items-start gap-3" data-id="element-5550">
                        <div className="w-8 h-8 rounded-lg bg-slate-900 border border-slate-800 flex items-center justify-center shrink-0" data-id="element-5551">
                          <Icon size={14} className={agent.color} data-id="element-5552" />
                        </div>
                        <div className="flex-1 min-w-0" data-id="element-5553">
                          <p className="text-sm font-bold text-slate-200 truncate" data-id="element-5554">
                            {agent.name}
                          </p>
                          <p className="text-[10px] text-slate-400 truncate mb-1" data-id="element-5555">
                            {agent.specialty}
                          </p>
                          <div className="flex items-center justify-between mt-2" data-id="element-5556">
                            <span className="text-xs font-mono text-slate-300" data-id="element-5557">
                              ${agent.price}/msg
                            </span>
                            {isComingSoon ? <span className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold" data-id="element-5558">
                                Pronto
                              </span> : isSelected ? <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded font-medium" data-id="element-5559">
                                Contratado
                              </span> : <span className="text-[10px] bg-slate-800 text-slate-400 px-2 py-0.5 rounded font-medium" data-id="element-5560">
                                Contratar
                              </span>}
                          </div>
                        </div>
                      </div>
                    </div>;
            })}
              </div>

              <div className="flex items-center justify-between pt-4 border-t border-slate-800" data-id="element-5561">
                <div className="flex items-center gap-3" data-id="element-5562">
                  <Button variant="outline" size="sm" onClick={goBack} className="border-slate-700 text-slate-400" data-id="element-5563">
                    <ChevronLeft size={16} data-id="element-5564" />
                  </Button>
                  <p className="text-sm text-slate-400" data-id="element-5565">
                    <strong className="text-slate-200" data-id="element-5566">
                      {selectedAgents.length}
                    </strong>{' '}
                    agentes seleccionados
                  </p>
                </div>
                <Button onClick={goNext} disabled={selectedAgents.length === 0} className="bg-emerald-600 hover:bg-emerald-500 px-8" data-id="element-5567">
                  Continuar <ChevronRight size={16} className="ml-2" data-id="element-5568" />
                </Button>
              </div>
            </motion.div>}

          {/* STEP 4: MÉTODO DE PAGO */}
          {currentStep === 4 && <motion.div key="step4" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5569">
              <div className="flex items-center gap-4 mb-8" data-id="element-5570">
                <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center text-amber-400" data-id="element-5571">
                  <CreditCard size={24} data-id="element-5572" />
                </div>
                <div data-id="element-5573">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5574">
                    Método de Pago
                  </h2>
                  <p className="text-slate-400" data-id="element-5575">
                    Agregá una tarjeta para cuando superes el free tier.
                  </p>
                </div>
              </div>

              <div className="space-y-5" data-id="element-5576">
                {/* Card Number */}
                <div data-id="element-5577">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5578">
                    Número de Tarjeta
                  </label>
                  <div className="relative" data-id="element-5579">
                    <input value={formatCardNumber(cardNumber)} onChange={e => setCardNumber(e.target.value.replace(/\D/g, '').slice(0, 16))} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 pr-16 text-slate-200 font-mono tracking-wider focus:outline-none focus:border-amber-500" placeholder="4242 4242 4242 4242" maxLength={19} data-id="element-5580" />

                    {cardBrand && <span className="absolute right-3 top-1/2 -translate-y-1/2 px-2 py-0.5 bg-slate-800 rounded text-[10px] font-bold text-white border border-slate-700" data-id="element-5581">
                        {cardBrand}
                      </span>}
                  </div>
                </div>

                {/* Expiry + CVC */}
                <div className="grid grid-cols-2 gap-4" data-id="element-5582">
                  <div data-id="element-5583">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5584">
                      Vencimiento
                    </label>
                    <input value={formatExpiry(cardExpiry)} onChange={e => setCardExpiry(e.target.value.replace(/\D/g, '').slice(0, 4))} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 font-mono focus:outline-none focus:border-amber-500" placeholder="MM/YY" maxLength={5} data-id="element-5585" />
                  </div>
                  <div data-id="element-5586">
                    <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5587">
                      CVC
                    </label>
                    <input value={cardCvc} onChange={e => setCardCvc(e.target.value.replace(/\D/g, '').slice(0, 4))} type="password" className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 font-mono focus:outline-none focus:border-amber-500" placeholder="•••" maxLength={4} data-id="element-5588" />
                  </div>
                </div>

                {/* Cardholder Name */}
                <div data-id="element-5589">
                  <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-5590">
                    Nombre en la Tarjeta
                  </label>
                  <input value={cardName} onChange={e => setCardName(e.target.value)} className="w-full bg-slate-950 border border-slate-700 rounded-lg px-4 py-3 text-slate-200 focus:outline-none focus:border-amber-500" placeholder="Como aparece en la tarjeta" data-id="element-5591" />
                </div>

                {/* Security Badges */}
                <div className="flex items-center gap-3" data-id="element-5592">
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-950/30 border border-emerald-900/40 rounded-lg text-[10px] font-medium text-emerald-400" data-id="element-5593">
                    <Shield size={10} data-id="element-5594" /> Encriptado SSL
                  </span>
                  <span className="flex items-center gap-1.5 px-2.5 py-1 bg-emerald-950/30 border border-emerald-900/40 rounded-lg text-[10px] font-medium text-emerald-400" data-id="element-5595">
                    <Lock size={10} data-id="element-5596" /> PCI Compliant
                  </span>
                </div>

                <p className="text-xs text-slate-500 flex items-center gap-1.5" data-id="element-5597">
                  <AlertTriangle size={10} className="text-amber-500" data-id="element-5598" />
                  No se realizará ningún cobro hasta que superes los {500}{' '}
                  mensajes gratis del mes.
                </p>
              </div>

              <div className="mt-8 flex items-center justify-between" data-id="element-5599">
                <Button variant="outline" size="sm" onClick={goBack} className="border-slate-700 text-slate-400" data-id="element-5600">
                  <ChevronLeft size={16} data-id="element-5601" />
                </Button>
                <div className="flex gap-4 items-center" data-id="element-5602">
                  <button onClick={() => {
                setSkippedPayment(true);
                goNext();
              }} className="text-sm text-slate-500 hover:text-slate-300" data-id="element-5603">
                    Omitir por ahora
                  </button>
                  <Button onClick={() => {
                setSkippedPayment(false);
                goNext();
              }} disabled={!isPaymentValid} className="bg-amber-600 hover:bg-amber-500 px-8" data-id="element-5604">
                    Guardar y Continuar{' '}
                    <ChevronRight size={16} className="ml-2" data-id="element-5605" />
                  </Button>
                </div>
              </div>
            </motion.div>}

          {/* STEP 5: API KEY */}
          {currentStep === 5 && <motion.div key="step5" custom={direction} variants={slideVariants} initial="enter" animate="center" exit="exit" transition={{
          duration: 0.2
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-8 shadow-xl" data-id="element-5606">
              <div className="flex items-center gap-4 mb-8" data-id="element-5607">
                <div className="w-12 h-12 rounded-full bg-cyan-500/20 flex items-center justify-center text-cyan-400" data-id="element-5608">
                  <Key size={24} data-id="element-5609" />
                </div>
                <div data-id="element-5610">
                  <h2 className="text-2xl font-bold text-slate-100" data-id="element-5611">
                    Tu API Key está lista
                  </h2>
                  <p className="text-slate-400" data-id="element-5612">
                    Usá esta key para conectar tus propios sistemas a VSM.
                  </p>
                </div>
              </div>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 mb-6 flex items-center justify-between" data-id="element-5613">
                <code className="text-cyan-400 font-mono text-sm truncate" data-id="element-5614">
                  {apiKey}
                </code>
                <button onClick={copyApiKey} className="p-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-400 transition-colors shrink-0 ml-3" data-id="element-5615">
                  {copied ? <Check size={16} className="text-emerald-400" data-id="element-5616" /> : <Copy size={16} data-id="element-5617" />}
                </button>
              </div>

              <div className="bg-[#0d1117] rounded-xl overflow-hidden border border-slate-800 mb-8" data-id="element-5618">
                <div className="px-4 py-2 bg-slate-900/50 border-b border-slate-800 text-xs text-slate-500 font-mono" data-id="element-5619">
                  Ejemplo de uso (cURL)
                </div>
                <pre className="p-4 text-xs font-mono overflow-x-auto leading-relaxed" data-id="element-5620">
                  <span className="text-pink-400" data-id="element-5621">curl</span>{' '}
                  <span className="text-slate-300" data-id="element-5622">
                    -X POST https://api.vsm.ai/v1/chat \
                  </span>
                  {'\n'}
                  <span className="text-slate-300" data-id="element-5623"> -H </span>
                  <span className="text-green-400" data-id="element-5624">
                    "Authorization: Bearer {apiKey.slice(0, 12)}..."
                  </span>
                  <span className="text-slate-300" data-id="element-5625"> \</span>
                  {'\n'}
                  <span className="text-slate-300" data-id="element-5626"> -H </span>
                  <span className="text-green-400" data-id="element-5627">
                    "Content-Type: application/json"
                  </span>
                  <span className="text-slate-300" data-id="element-5628"> \</span>
                  {'\n'}
                  <span className="text-slate-300" data-id="element-5629"> -d </span>
                  <span className="text-yellow-300" data-id="element-5630">
                    {'\'{"message": "¿Cuántos bugs hay?", "agent": "scrum-master"}\''}
                  </span>
                </pre>
              </div>

              <p className="text-xs text-slate-500 mb-6" data-id="element-5631">
                Podés generar más keys desde Configuración → Cuenta.
              </p>

              <div className="flex items-center justify-between" data-id="element-5632">
                <Button variant="outline" size="sm" onClick={goBack} className="border-slate-700 text-slate-400" data-id="element-5633">
                  <ChevronLeft size={16} data-id="element-5634" />
                </Button>
                <Button onClick={goNext} className="bg-cyan-600 hover:bg-cyan-500 px-8" data-id="element-5635">
                  Continuar <ChevronRight size={16} className="ml-2" data-id="element-5636" />
                </Button>
              </div>
            </motion.div>}

          {/* STEP 6: SUCCESS */}
          {currentStep === 6 && <motion.div key="step6" initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} className="bg-slate-900 border border-slate-800 rounded-2xl p-10 shadow-xl text-center" data-id="element-5637">
              <motion.div initial={{
            scale: 0
          }} animate={{
            scale: 1
          }} transition={{
            type: 'spring',
            stiffness: 200,
            damping: 15
          }} className="w-24 h-24 rounded-full bg-emerald-500/20 flex items-center justify-center text-emerald-400 mx-auto mb-6 relative" data-id="element-5638">
                <div className="absolute inset-0 rounded-full bg-emerald-400/20 animate-ping" data-id="element-5639" />
                <CheckCircle2 size={48} data-id="element-5640" />
              </motion.div>

              <h2 className="text-3xl font-bold text-slate-100 mb-2" data-id="element-5641">
                ¡Todo Listo!
              </h2>
              <p className="text-slate-400 mb-8 max-w-sm mx-auto" data-id="element-5642">
                Tu workspace está configurado y los agentes están listos para
                trabajar.
              </p>

              <div className="bg-slate-950 border border-slate-800 rounded-xl p-6 text-left max-w-sm mx-auto mb-8 space-y-3" data-id="element-5643">
                {[{
              delay: 0.2,
              text: <>
                        Empresa configurada:{' '}
                        <strong className="text-slate-100" data-id="element-5644">
                          {companyName || 'Workium AI'}
                        </strong>
                      </>,
              ok: true
            }, {
              delay: 0.3,
              text: 'Jira conectado',
              ok: integrations.jira
            }, {
              delay: 0.4,
              text: <>
                        <strong className="text-slate-100" data-id="element-5645">
                          {selectedAgents.length}
                        </strong>{' '}
                        agentes contratados
                      </>,
              ok: true
            }, {
              delay: 0.5,
              text: skippedPayment ? 'Pago pendiente — configuralo desde Facturación' : 'Método de pago verificado',
              ok: !skippedPayment
            }, {
              delay: 0.6,
              text: 'API Key generada',
              ok: true
            }].map((item, i) => <motion.div key={i} initial={{
              opacity: 0,
              x: -10
            }} animate={{
              opacity: 1,
              x: 0
            }} transition={{
              delay: item.delay
            }} className="flex items-center gap-3 text-sm" data-id="element-5646">
                    {item.ok ? <CheckCircle2 size={16} className="text-emerald-400 shrink-0" data-id="element-5647" /> : <AlertTriangle size={16} className="text-amber-400 shrink-0" data-id="element-5648" />}
                    <span className={item.ok ? 'text-slate-300' : 'text-amber-300'} data-id="element-5649">
                      {item.text}
                    </span>
                  </motion.div>)}
              </div>

              <div className="flex flex-col gap-3 max-w-sm mx-auto" data-id="element-5650">
                <Button onClick={onComplete} className="w-full bg-emerald-600 hover:bg-emerald-500 py-6 text-lg" data-id="element-5651">
                  Ir al Dashboard
                </Button>
                <button className="text-sm text-slate-500 hover:text-slate-300 transition-colors" data-id="element-5652">
                  Explorar la documentación
                </button>
              </div>
            </motion.div>}
        </AnimatePresence>
      </div>
    </div>;
}